import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { BOAT_TYPES } from '../../../utils/boatTypes';
import { formatDisplayDate } from '../../../utils/timeUtils';
import { planningAPI } from '../../../services/api';

const SummaryValidationStep = ({
  session,
  timeSlot,
  selectedCrews,
  crewLineups,
  selectedCoaches,
  crews,
  coaches,
  onSchedule,
  onBack,
  loading,
}) => {
  const [validationResult, setValidationResult] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [warningsAcknowledged, setWarningsAcknowledged] = useState(false);

  // Real-time validation whenever selections change
  useEffect(() => {
    if (selectedCrews.length > 0 || selectedCoaches.length > 0) {
      performValidation();
    }
    // Reset warnings acknowledgment when validation changes
    setWarningsAcknowledged(false);
  }, [selectedCrews, crewLineups, selectedCoaches]);

  const performValidation = async () => {
    try {
      setValidationLoading(true);

      // Build configuration data for validation
      const configurationData = {
        crews: [],
        coaches: selectedCoaches,
        start_time: timeSlot?.time,
        date: timeSlot?.date,
      };

      selectedCrews.forEach((crewId) => {
        const crew = crews.find((c) => c.id === crewId);
        if (!crew) return;

        const crewConfig = {
          crew_id: crewId,
          lineup: [],
        };

        if (crew.is_experimental) {
          const lineup = crewLineups[crewId] || [];
          lineup.forEach((rower, index) => {
            if (rower) {
              // Cox position is at index 0 and should have seat_number 0
              // Regular rower positions start at index 1 for boats with cox, or index 0 for boats without cox
              const boatType = BOAT_TYPES[crew.boat_type];
              const seat_number = boatType?.hasCox ? index : index + 1;

              crewConfig.lineup.push({
                rower_id: rower.id,
                first_name: rower.first_name,
                last_name: rower.last_name,
                seat_number,
                side: rower.preferred_side || 'none',
              });
            }
          });
        } else {
          // For non-experimental crews, use existing lineup
          if (crew.current_lineup && crew.current_lineup.length > 0) {
            crew.current_lineup.forEach((assignment) => {
              crewConfig.lineup.push({
                rower_id: assignment.user_id,
                first_name: assignment.first_name,
                last_name: assignment.last_name,
                seat_number: assignment.seat_number,
                side: assignment.side || 'none',
              });
            });
          } else if (crew.members && crew.members.length > 0) {
            // Fallback to crew members if no current_lineup
            crew.members.forEach((member) => {
              if (member.user) {
                crewConfig.lineup.push({
                  rower_id: member.user.id,
                  first_name: member.user.first_name,
                  last_name: member.user.last_name,
                  seat_number: member.seat_number || 0,
                  side: member.user.preferred_side || 'none',
                });
              }
            });
          }
        }

        configurationData.crews.push(crewConfig);
      });

      const result = await planningAPI.validateSessionConfiguration(session.id, configurationData);
      setValidationResult(result);
    } catch (error) {
      console.error('Validation error:', error);
      setValidationResult({
        is_valid: false,
        errors: [{ type: 'validation_error', message: 'Failed to validate configuration' }],
        warnings: [],
        suggestions: [],
      });
    } finally {
      setValidationLoading(false);
    }
  };

  const getCrewSummary = () => {
    return selectedCrews.map((crewId) => {
      const crew = crews.find((c) => c.id === crewId);
      const lineup = crewLineups[crewId] || [];
      return { crew, lineup };
    });
  };

  const getCoachSummary = () => {
    return selectedCoaches.map((coachId) => coaches.find((c) => c.id === coachId)).filter(Boolean);
  };

  const getTotalParticipants = () => {
    const currentCrewSummary = getCrewSummary();
    let total = 0;

    currentCrewSummary.forEach(({ crew, lineup }) => {
      if (crew.is_experimental) {
        total += lineup.filter(Boolean).length;
      } else if (crew.current_lineup && crew.current_lineup.length > 0) {
        total += crew.current_lineup.length;
      } else {
        total += crew.members?.length || 0;
      }
    });

    return total;
  };

  const getSelectedRowers = () => {
    const rowers = [];
    selectedCrews.forEach((crewId) => {
      const crew = crews.find((c) => c.id === crewId);
      if (!crew) return;

      if (crew.is_experimental) {
        const lineup = crewLineups[crewId] || [];
        lineup.forEach((rower) => {
          if (rower) {
            rowers.push(rower);
          }
        });
      } else if (crew.current_lineup && crew.current_lineup.length > 0) {
        // Use current_lineup data for non-experimental crews if available
        crew.current_lineup.forEach((member) => {
          rowers.push({
            id: member.user_id,
            first_name: member.first_name,
            last_name: member.last_name,
            name: `${member.first_name} ${member.last_name}`,
            seat_number: member.seat_number,
            side: member.side,
          });
        });
      } else if (crew.members) {
        // Fallback to crew.members for non-experimental crews
        crew.members.forEach((member) => {
          if (member.user) {
            rowers.push({
              id: member.user.id,
              first_name: member.user.first_name,
              last_name: member.user.last_name,
              name: `${member.user.first_name} ${member.user.last_name}`,
              seat_number: member.seat_number,
              side: member.user.preferred_side,
            });
          }
        });
      }
    });
    return rowers;
  };

  const canSchedule = () => {
    if (!validationResult) return false;
    if (!validationResult.is_valid) return false;

    // Filter out merge notifications from warnings that need acknowledgment
    const warningsNeedingAcknowledgment =
      validationResult.warnings?.filter((w) => !w.is_merge_notification) || [];

    // If there are warnings, they must be acknowledged
    if (warningsNeedingAcknowledgment.length > 0) {
      return warningsAcknowledged;
    }

    return true;
  };

  const crewSummaryData = getCrewSummary();
  const coachSummaryData = getCoachSummary();
  const selectedRowers = getSelectedRowers();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-lg font-medium text-gray-900">Review & Schedule Session</h3>
        <p className="text-sm text-gray-600">
          Review all details before scheduling the training session.
        </p>
      </div>

      <div className="rounded-lg bg-gray-50 p-4">
        <h4 className="mb-3 font-medium text-gray-900">Session Overview</h4>
        <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2 lg:grid-cols-3">
          <div>
            <span className="text-gray-600">Session:</span>
            <span className="ml-2 font-medium">{session.title}</span>
          </div>
          <div>
            <span className="text-gray-600">Date & Time:</span>
            <span className="ml-2 font-medium">
              {formatDisplayDate(new Date(timeSlot.date))} at {timeSlot.time}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Duration:</span>
            <span className="ml-2 font-medium">{session.duration} minutes</span>
          </div>
          <div>
            <span className="text-gray-600">Total Participants:</span>
            <span className="ml-2 font-medium">{getTotalParticipants()} rowers</span>
          </div>
          <div>
            <span className="text-gray-600">Crews:</span>
            <span className="ml-2 font-medium">{selectedCrews.length}</span>
          </div>
          <div>
            <span className="text-gray-600">Coaches:</span>
            <span className="ml-2 font-medium">{selectedCoaches.length}</span>
          </div>
          {session.location && (
            <div className="md:col-span-2 lg:col-span-3">
              <span className="text-gray-600">Location:</span>
              <span className="ml-2 font-medium">{session.location}</span>
            </div>
          )}
          {session.description && (
            <div className="md:col-span-2 lg:col-span-3">
              <span className="text-gray-600">Description:</span>
              <span className="ml-2">{session.description}</span>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="mt-4 border-t border-gray-200 pt-3">
          <div className="flex items-center justify-between text-xs text-gray-600">
            <div className="flex gap-4">
              <span>
                ✅{' '}
                {
                  crewSummaryData.filter(({ crew, lineup }) => {
                    const boatType = BOAT_TYPES[crew.boat_type];
                    const requiredPositions = (boatType?.seats || 0) + (boatType?.hasCox ? 1 : 0);

                    if (crew.is_experimental) {
                      return lineup.filter(Boolean).length === requiredPositions;
                    } else if (crew.current_lineup && crew.current_lineup.length > 0) {
                      return crew.current_lineup.length >= requiredPositions;
                    } else {
                      return (crew.members?.length || 0) >= requiredPositions;
                    }
                  }).length
                }{' '}
                complete crews
              </span>
              <span>
                ⚠️{' '}
                {
                  coachSummaryData.filter(
                    (c) =>
                      c.availability_status === 'coaching_other_crews' ||
                      c.availability_status === 'not_available' ||
                      c.availability_status === 'conflict'
                  ).length
                }{' '}
                coaches with conflicts
              </span>
            </div>
            <div className="font-medium">
              {canSchedule() ? '✅ Ready to schedule' : '⚠️ Review required'}
            </div>
          </div>
        </div>
      </div>

      {/* Validation Results */}
      {validationLoading && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="text-blue-800">Validating configuration...</div>
        </div>
      )}

      {validationResult && (
        <div className="space-y-3">
          {/* Errors */}
          {validationResult.errors && validationResult.errors.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <h4 className="mb-2 font-medium text-red-800">⚠️ Errors (Must Fix)</h4>
              <ul className="space-y-1 text-sm text-red-700">
                {validationResult.errors.map((error, index) => (
                  <li key={index}>• {error.message}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Merge Detection - Special handling for merge notifications */}
          {validationResult.warnings &&
            validationResult.warnings.some((w) => w.is_merge_notification) && (
              <div className="mb-4 rounded-lg border border-blue-300 bg-blue-50 p-4">
                <h4 className="mb-2 flex items-center font-medium text-blue-800">
                  <span className="mr-2">🔄</span>
                  Session Merge Detected
                </h4>
                {validationResult.warnings
                  .filter((w) => w.is_merge_notification)
                  .map((warning, index) => (
                    <div key={index} className="mb-2 text-sm text-blue-700">
                      {warning.message}
                    </div>
                  ))}
                <div className="mt-2 rounded bg-blue-100 px-2 py-1 text-xs text-blue-600">
                  ✅ This is automatic - no action required. Your crews will be added to the
                  existing session.
                </div>
              </div>
            )}

          {/* Warnings */}
          {validationResult.warnings &&
            validationResult.warnings.filter((w) => !w.is_merge_notification).length > 0 && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <h4 className="mb-2 font-medium text-yellow-800">⚠️ Warnings</h4>
                <ul className="space-y-1 text-sm text-yellow-700">
                  {validationResult.warnings
                    .filter((w) => !w.is_merge_notification)
                    .map((warning, index) => (
                      <li key={index}>• {warning.message}</li>
                    ))}
                </ul>
              </div>
            )}

          {/* Suggestions */}
          {validationResult.suggestions && validationResult.suggestions.length > 0 && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <h4 className="mb-2 font-medium text-green-800">💡 Suggestions</h4>
              <ul className="space-y-1 text-sm text-green-700">
                {validationResult.suggestions.map((suggestion, index) => (
                  <li key={index}>• {suggestion.message}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div>
        <h4 className="mb-3 font-medium text-gray-900">Crew Assignments</h4>
        <div className="space-y-3">
          {crewSummaryData.map(({ crew, lineup }) => {
            const boatType = BOAT_TYPES[crew.boat_type];
            return (
              <div key={crew.id} className="rounded-lg border border-gray-200 p-3">
                <div className="mb-2 font-medium text-gray-900">
                  {crew.name} ({boatType?.name || crew.boat_type})
                </div>
                {crew.is_experimental ? (
                  <div className="text-sm">
                    {lineup.filter(Boolean).length > 0 ? (
                      <div className="space-y-1">
                        {lineup.map((rower, index) => {
                          if (!rower) return null;
                          const seatNumber =
                            boatType?.hasCox && index === 0
                              ? 'Cox'
                              : `Seat ${boatType?.hasCox ? index : index + 1}`;

                          return (
                            <div key={index} className="flex items-center justify-between">
                              <span className="text-gray-600">{seatNumber}</span>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{rower.name}</span>
                                {rower.preferred_side && rower.preferred_side !== 'none' && (
                                  <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                    {rower.preferred_side}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        <div className="border-t pt-1 text-xs text-gray-500">
                          {lineup.filter(Boolean).length} /{' '}
                          {(boatType?.seats || 0) + (boatType?.hasCox ? 1 : 0)} positions filled
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500">No rowers assigned</div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm">
                    {crew.current_lineup && crew.current_lineup.length > 0 ? (
                      <div className="space-y-1">
                        {crew.current_lineup
                          .sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0))
                          .map((member, index) => (
                            <div key={index} className="flex items-center justify-between">
                              <span className="text-gray-600">
                                {member.seat_number === 0 ? 'Cox' : `Seat ${member.seat_number}`}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">
                                  {member.first_name} {member.last_name}
                                </span>
                                {member.side && member.side !== 'none' && (
                                  <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                    {member.side}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        <div className="border-t pt-1 text-xs text-gray-500">
                          {crew.current_lineup.length} /{' '}
                          {(boatType?.seats || 0) + (boatType?.hasCox ? 1 : 0)} positions filled
                        </div>
                      </div>
                    ) : crew.members && crew.members.length > 0 ? (
                      <div className="space-y-1">
                        {crew.members
                          .sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0))
                          .map((member, index) => (
                            <div key={index} className="flex items-center justify-between">
                              <span className="text-gray-600">
                                {member.seat_number === 0
                                  ? 'Cox'
                                  : member.seat_number
                                    ? `Seat ${member.seat_number}`
                                    : `Seat ${index + 1}`}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">
                                  {member.user?.first_name} {member.user?.last_name}
                                </span>
                                {member.user?.preferred_side &&
                                  member.user.preferred_side !== 'none' && (
                                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                      {member.user.preferred_side}
                                    </span>
                                  )}
                              </div>
                            </div>
                          ))}
                        <div className="border-t pt-1 text-xs text-gray-500">
                          {crew.members.length} /{' '}
                          {(boatType?.seats || 0) + (boatType?.hasCox ? 1 : 0)} positions filled
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500">No rowers assigned</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="mb-3 font-medium text-gray-900">Coach Assignments</h4>
        {coachSummaryData.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {coachSummaryData.map((coach) => {
              const getAvailabilityIcon = () => {
                switch (coach.availability_status) {
                  case 'fully_available':
                    return '✅';
                  case 'partly_available':
                    return '⚠️';
                  case 'coaching_other_crews':
                    return '🔄';
                  case 'not_available':
                    return '❌';
                  case 'conflict':
                    return '⚠️';
                  default:
                    return '❓';
                }
              };

              const getStatusColor = () => {
                switch (coach.availability_status) {
                  case 'fully_available':
                    return 'border-green-200 bg-green-50';
                  case 'partly_available':
                    return 'border-yellow-200 bg-yellow-50';
                  case 'coaching_other_crews':
                    return 'border-blue-200 bg-blue-50';
                  case 'not_available':
                    return 'border-red-200 bg-red-50';
                  case 'conflict':
                    return 'border-red-300 bg-red-100';
                  default:
                    return 'border-gray-200 bg-gray-50';
                }
              };

              const getStatusDescription = () => {
                switch (coach.availability_status) {
                  case 'fully_available':
                    return 'Fully available';
                  case 'partly_available':
                    return 'Partially available';
                  case 'coaching_other_crews':
                    return 'Already coaching other crews';
                  case 'not_available':
                    return 'Not available';
                  case 'conflict':
                    return 'Has conflicting session';
                  default:
                    return 'Unknown status';
                }
              };

              return (
                <div key={coach.id} className={`rounded-lg border p-3 ${getStatusColor()}`}>
                  <div className="mb-1 flex items-center justify-between">
                    <div className="font-medium text-gray-900">
                      {coach.first_name && coach.last_name
                        ? `${coach.first_name} ${coach.last_name}`
                        : coach.name || 'Unknown Coach'}
                    </div>
                    <span className="text-lg">{getAvailabilityIcon()}</span>
                  </div>
                  <div className="text-sm text-gray-600">{getStatusDescription()}</div>

                  {/* Show detailed conflict information for coaches with conflicts */}
                  {coach.availability_status === 'conflict' &&
                    coach.availability_details?.conflicts && (
                      <div className="mt-2 rounded border border-red-100 bg-red-50 p-2 text-xs">
                        <div className="mb-1 font-medium text-red-700">Conflicting Sessions:</div>
                        {coach.availability_details.conflicts.map((conflict, index) => {
                          // Format time without seconds
                          const formatTime = (timeString) => {
                            if (!timeString) return '';
                            return timeString.substring(0, 5); // Remove seconds (HH:MM:SS -> HH:MM)
                          };

                          return (
                            <div key={index} className="mb-1 text-red-700">
                              <div className="font-medium">{conflict.session_name}</div>
                              <div className="text-red-600">
                                {conflict.crew_name && (
                                  <div className="mb-1">
                                    <span className="font-medium">Crew:</span> {conflict.crew_name}
                                  </div>
                                )}
                                <div>
                                  <span className="font-medium">Time:</span>{' '}
                                  {formatTime(conflict.start_time)}-{formatTime(conflict.end_time)}
                                  {conflict.location && ` • ${conflict.location}`}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                  {/* Legacy conflicts display for backward compatibility */}
                  {coach.conflicts &&
                    coach.conflicts.length > 0 &&
                    coach.availability_status !== 'conflict' && (
                      <div className="mt-1 text-xs text-gray-500">
                        Conflicts with {coach.conflicts.length} session(s)
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-gray-500">No coaches assigned</div>
        )}
      </div>

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Back
        </button>

        <div className="flex flex-col items-end gap-3">
          {/* Schedule Button with Dynamic Behavior */}
          {validationResult &&
          validationResult.warnings &&
          validationResult.warnings.filter((w) => !w.is_merge_notification).length > 0 &&
          !warningsAcknowledged ? (
            <button
              onClick={() => setWarningsAcknowledged(true)}
              disabled={loading || !validationResult?.is_valid}
              className="flex items-center gap-2 rounded-lg bg-yellow-500 px-4 py-2 text-white hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Click to Acknowledge
            </button>
          ) : (
            <button
              onClick={onSchedule}
              disabled={loading || !canSchedule()}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <svg
                    className="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {validationResult?.warnings?.some((w) => w.is_merge_notification)
                    ? 'Merging...'
                    : 'Scheduling...'}
                </>
              ) : validationResult?.warnings?.some((w) => w.is_merge_notification) ? (
                'Merge Crews into Existing Session'
              ) : (
                'Schedule'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

SummaryValidationStep.propTypes = {
  session: PropTypes.object.isRequired,
  timeSlot: PropTypes.object.isRequired,
  selectedCrews: PropTypes.array.isRequired,
  crewLineups: PropTypes.object.isRequired,
  selectedCoaches: PropTypes.array.isRequired,
  crews: PropTypes.array.isRequired,
  coaches: PropTypes.array.isRequired,
  // availableRowers: PropTypes.array.isRequired, // Marked as unused
  onSchedule: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default SummaryValidationStep;
